import { Box } from "@mui/material";
import type { TFunction } from "i18next";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import type { TranslationKeys } from "../../i18n/types";
import { NurseTrainingStatus, type OvertimeContribution } from "../../utils/models";
import type { TrainingPayStatus, WageSettingsTarget } from "../team/WageSettings/deepLink";
import { moneyOrHidden, OTHER_DEPARTMENT_MARKER, signedMoneyOrHidden, TRAINING_PAY_LINK_LABEL } from "./consts";
import { RuleCard } from "./RuleCard/RuleCard";
import { RuleNameText } from "./RuleCard/RuleNameText";
import type { RuleSubject } from "./RuleCard/ruleCardSubjects";
import { useCompositionText } from "./useCompositionText";
import { WageSettingsLink } from "./WageSettingsLink";
import { type BaseBasis, type CompositionLine, isOtherDepartmentRule } from "./wageComposition";

const BASE_LABEL: Readonly<Record<Exclude<BaseBasis, "agency">, TranslationKeys>> = {
	employee: "wageBreakdown.base.employee",
	estimated: "wageBreakdown.base.estimated",
	salaried: "wageBreakdown.base.salaried",
	override: "wageBreakdown.base.override",
};

type LineContent = { label: ReactNode; detail?: string; rate: string };

export type LineContext = {
	rowId: number;
	/** The line's position in its segment: part of its link's test id. */
	index: number;
	agencyName: string | null;
	canViewWage: boolean;
	canOpenWageSettings: boolean;
};

/** `viewedDepartmentId`: the department on screen, whose Wage Settings every link opens. */
type Ctx = LineContext & { t: TFunction; text: ReturnType<typeof useCompositionText>; viewedDepartmentId: string };

/**
 * A rule's name, linked to its Wage Settings row when the viewer can open it, and either way opening a card
 * with that rule's row (`subject`). A blank name still gets a label.
 */
const ruleLabel = (ctx: Ctx, name: string, target: WageSettingsTarget, subject: RuleSubject): ReactNode => {
	const label = name.trim() === "" ? ctx.t("wageBreakdown.rule.unnamed") : name;
	const card = <RuleCard subjects={[subject]} dataTestId={`wage-breakdown-rule-card-${ctx.rowId}-${ctx.index}`} />;
	if (!ctx.canOpenWageSettings) return <RuleNameText label={label} card={card} dataTestId={`wage-breakdown-rule-name-${ctx.rowId}-${ctx.index}`} />;
	return (
		<WageSettingsLink
			target={target}
			label={label}
			ariaLabel={ctx.t("wageBreakdown.openInWageSettings", { rateType: label })}
			dataTestId={`wage-breakdown-rule-link-${ctx.rowId}-${ctx.index}`}
			emphasis="subtle"
			hoverCard={card}
		/>
	);
};

/**
 * Why the base stands alone, where that is not obvious: a salary is never adjusted by rules; an agency rate
 * can be, so it says so only on a segment no hours-rate rule matched.
 */
const baseDetail = (ctx: Ctx, line: Extract<CompositionLine, { type: "base" }>): string | undefined => {
	if (line.basis === "salaried") return ctx.t("wageBreakdown.base.salariedDetail");
	if (line.basis === "agency" && !line.hoursRatesMatched) return ctx.t("wageBreakdown.base.agencyNoRules");
	return undefined;
};

const baseContent = (ctx: Ctx, line: Extract<CompositionLine, { type: "base" }>): LineContent => {
	const agencyLabel = ctx.agencyName ? ctx.t("wageBreakdown.base.agencyNamed", { agency: ctx.agencyName }) : ctx.t("wageBreakdown.base.agency");
	return {
		label: line.basis === "agency" ? agencyLabel : ctx.t(BASE_LABEL[line.basis]),
		detail: baseDetail(ctx, line),
		rate: moneyOrHidden(line.cents, ctx.canViewWage),
	};
};

/** Training / orientation pay IS the base line: titled like its card, saying what it replaced. */
const trainingContent = (ctx: Ctx, line: Extract<CompositionLine, { type: "training" }>): LineContent => {
	const status: TrainingPayStatus = line.contribution.kind === "training" ? NurseTrainingStatus.Trainee : NurseTrainingStatus.Orientation;
	const label = ctx.t(TRAINING_PAY_LINK_LABEL[status]);
	const target: WageSettingsTarget = { section: "training-orientation", trainingStatus: status };
	return {
		label: ctx.canOpenWageSettings ? (
			<WageSettingsLink
				target={target}
				label={label}
				ariaLabel={ctx.t("wageBreakdown.openInWageSettings", { rateType: label })}
				// Per row: the line repeats on every segment of the shift.
				dataTestId={`wage-breakdown-subtable-training-pay-link-${ctx.rowId}`}
				emphasis="subtle"
			/>
		) : (
			label
		),
		detail: ctx.text.trainingDetail(line.contribution, moneyOrHidden(line.ownBaseCents, ctx.canViewWage), moneyOrHidden(line.cents, ctx.canViewWage)),
		rate: moneyOrHidden(line.cents, ctx.canViewWage),
	};
};

const overtimeTarget = (c: OvertimeContribution): WageSettingsTarget => ({
	section: "overtime",
	overtimeKind: c.otKind,
	rule: c.name,
	...(c.ruleId === null ? {} : { ruleId: c.ruleId }),
});

/** Lines that name a Wage Settings rule: an hours-rate rule or the overtime rule. */
const ruleContent = (ctx: Ctx, line: Extract<CompositionLine, { type: "hours_rate" | "overtime" }>): LineContent => {
	const rate = signedMoneyOrHidden(line.cents, ctx.canViewWage);
	if (line.type === "overtime") {
		// A float-pool nurse's OT rule can live in their home department. A link would open this
		// department's settings, where the rule isn't, so it is named without one and marked for the
		// panel's footnote. A line with no captured department is only left unlinked.
		const isViewedDepartmentRule = line.contribution.departmentId === ctx.viewedDepartmentId;
		const label = ruleLabel(
			{ ...ctx, canOpenWageSettings: ctx.canOpenWageSettings && isViewedDepartmentRule },
			line.contribution.name,
			overtimeTarget(line.contribution),
			{ kind: "overtime", contribution: line.contribution },
		);
		return {
			label: isOtherDepartmentRule(line.contribution, ctx.viewedDepartmentId) ? (
				<>
					{label}
					{OTHER_DEPARTMENT_MARKER}
				</>
			) : (
				label
			),
			detail: ctx.text.overtimeDetail(line.contribution, line.band),
			rate,
		};
	}
	const { name, ruleId } = line.contribution;
	const target: WageSettingsTarget = { section: "hours-rate", rule: name, ...(ruleId === null ? {} : { ruleId }) };
	return {
		label: ruleLabel(ctx, name, target, { kind: "hours_rate", contribution: line.contribution }),
		detail: ctx.text.hoursRateScope(line.contribution),
		rate,
	};
};

/** Lines no Wage Settings rule is behind: holiday (a support-only setting), compounding, rounding. */
const unlinkedContent = (ctx: Ctx, line: Extract<CompositionLine, { type: "holiday" | "compounding" | "rounding" }>): LineContent => {
	const rate = signedMoneyOrHidden(line.cents, ctx.canViewWage);
	switch (line.type) {
		case "holiday":
			return { label: ctx.t("wageBreakdown.holiday"), detail: ctx.text.holidayPercent(line.contribution.value), rate };
		case "compounding":
			return { label: ctx.t("wageBreakdown.compounding"), detail: ctx.t("wageBreakdown.compoundingDetail"), rate };
		case "rounding":
			return {
				label: (
					<Box component="span" sx={{ fontStyle: "italic" }}>
						{ctx.t("wageBreakdown.rounding")}
					</Box>
				),
				detail: ctx.t("wageBreakdown.roundingRateDetail"),
				rate,
			};
	}
};

/** What one composition line says: its label (linked when it names a rule), its detail and its $/h. */
export const useCompositionLineContent = (line: CompositionLine, context: LineContext): LineContent => {
	const { t } = useTranslation();
	const text = useCompositionText();
	const { chosenDepartment } = useHospitalData();
	const ctx: Ctx = { ...context, t, text, viewedDepartmentId: chosenDepartment.department_id };
	switch (line.type) {
		case "base":
			return baseContent(ctx, line);
		case "training":
			return trainingContent(ctx, line);
		case "hours_rate":
		case "overtime":
			return ruleContent(ctx, line);
		case "holiday":
		case "compounding":
		case "rounding":
			return unlinkedContent(ctx, line);
	}
};
